import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageCard from "@/components/ImageCard";
import Lightbox from "@/components/Lightbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, HistoryIcon, LogIn, ImageOff } from "lucide-react";

export default function History() {
  const { user, loading: authLoading, login } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/images/mine");
      setImages(res.data);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
    else if (!authLoading) setLoading(false);
  }, [user, authLoading, load]);

  const togglePublic = async (img) => {
    try {
      const res = await api.patch(`/images/${img.short_code}`, { is_public: !img.is_public });
      setImages((prev) => prev.map((i) => (i.id === img.id ? res.data : i)));
      toast.success(res.data.is_public ? "Shared to public gallery" : "Made private");
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (img) => {
    try {
      await api.delete(`/images/${img.short_code}`);
      setImages((prev) => prev.filter((i) => i.id !== img.id));
      toast.success("Image removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-40 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 flex flex-col items-center text-center" data-testid="history-signin-gate">
        <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
          <LogIn className="h-6 w-6 text-accent" />
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Sign in to see your history</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">
          Keep track of every image you upload and choose which ones to share to the public gallery.
        </p>
        <Button onClick={login} className="mt-6 rounded-full" data-testid="history-signin-btn">
          Sign in with Google
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex items-center gap-3 mb-8">
        <HistoryIcon className="h-6 w-6 text-accent" />
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">My History</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{images.length} image{images.length !== 1 ? "s" : ""} · toggle public to share.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : images.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-28 text-center" data-testid="history-empty">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <ImageOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="font-heading font-semibold text-lg">No uploads yet</p>
          <p className="text-sm text-muted-foreground mt-1">Head to Upload to add your first image.</p>
        </div>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4" data-testid="history-grid">
          {images.map((img) => (
            <ImageCard
              key={img.id}
              image={img}
              owner
              onClick={setSelected}
              onTogglePublic={togglePublic}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      <Lightbox image={selected} onOpenChange={setSelected} />
    </div>
  );
}
